import { ProductDataError, productData } from '@/lib/api/product-data-client'

/**
 * Client-side AI API Service
 *
 * Provides HTTP client methods for AI extraction endpoints.
 * For server-side usage, import use cases directly from @/features/ai
 */

export interface NaicsDeterminationResponse {
  result: { naicsCode: string; naicsDescription: string } | null
  metadata?: { usedModel: string; duration: number; timestamp: string }
  error?: string
  canRetry?: boolean
}

const DEFAULT_POLL_INTERVAL_MS = 1_500
const DEFAULT_DEADLINE_MS = 100_000

export interface ExtractionOptions {
  pollIntervalMs?: number
  deadlineMs?: number
}

interface AiJob<T> {
  jobId: string
  kind: string
  state: 'pending' | 'done' | 'failed'
  result: T | null
  error: string | null
}

export interface EinExtractionResult {
  ein: string
}

export interface AddressExtractionResult {
  formationState?: string
  formationDate?: string
  formationDateUnreadable?: boolean
  stateId?: string
  lowConfidence?: boolean
  confidenceUnknown?: boolean
  primaryAddress?: { street?: string; city?: string; state?: string; zipCode?: string; apartment?: string }
  mailingAddress?: { street?: string; city?: string; state?: string; zipCode?: string; apartment?: string }
}

/**
 * The Worker reads a PDF asynchronously (goal 253): submit returns 202 with a
 * job id, and the result arrives on a poll. Goal 253 kept a server-side poll
 * inside Product's passthrough so the wizard's synchronous contract survived;
 * that passthrough is gone now, so the loop lives here, in the browser, where
 * it costs no serverless wall-clock.
 *
 * The deadline is checked AFTER a `pending` read rather than before the
 * first, so a `deadlineMs: 0` test still performs exactly one poll and then
 * times out, instead of never polling at all.
 */
async function extractViaJob<T>(
  path: string,
  file: File,
  options: ExtractionOptions = {},
): Promise<{ result: T | null; parseError?: string }> {
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS
  const deadline = Date.now() + (options.deadlineMs ?? DEFAULT_DEADLINE_MS)

  const form = new FormData()
  form.append('file', file, file.name)
  const { jobId } = await productData<{ jobId: string }>(path, {
    method: 'POST',
    body: form,
  })

  for (;;) {
    const job = await productData<AiJob<T>>(`/api/customer/ai/jobs/${encodeURIComponent(jobId)}`)
    if (job.state === 'done') return { result: job.result }
    if (job.state === 'failed') {
      if (job.error === 'AI_PARSE_ERROR') {
        return {
          result: null,
          parseError: 'Failed to parse model output as JSON',
        }
      }
      throw new ProductDataError(
        500,
        job.error ?? 'AI_FAILED',
        'Unable to read the document. Please try again or enter the values manually.',
      )
    }
    if (Date.now() >= deadline) {
      throw new ProductDataError(504, 'TIMEOUT', 'Extraction timed out. Please try again or enter the values manually.')
    }
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
  }
}

export class AiApiService {
  static async ExtractEin(file: File, options?: ExtractionOptions) {
    return extractViaJob<EinExtractionResult>('/api/customer/ai/extract-ein', file, options)
  }

  static async ExtractAddresses(file: File, options?: ExtractionOptions) {
    return extractViaJob<AddressExtractionResult>('/api/customer/ai/extract-addresses', file, options)
  }

  /**
   * The Worker owns the model call, the input validation and the response
   * body (goal 239) at `POST /api/customer/ai/determine-naics`. It answers
   * the LEGACY shape rather than the `{success,data}` envelope — a success
   * (or a model/gateway failure, still 200) is `{ result, metadata }` /
   * `{ result: null, error, canRetry }`, so `productData` passes both
   * through untouched.
   *
   * `productData` still throws for a bad-input 400, an auth/rate-limit
   * 401/429/503, or a network failure. There is no browser caller of this
   * method today, but it is kept to the same "never a thrown 5xx" contract
   * the old passthrough had (controller ruling R12): any thrown
   * `ProductDataError` collapses into the same retryable failure body the
   * Worker itself answers for a model/gateway failure, so a future caller
   * never needs its own try/catch.
   */
  static async DetermineNaics(companyDescription: string): Promise<NaicsDeterminationResponse> {
    try {
      return await productData<NaicsDeterminationResponse>('/api/customer/ai/determine-naics', {
        method: 'POST',
        body: JSON.stringify({ companyDescription }),
      })
    } catch (error) {
      const message = error instanceof ProductDataError ? error.message : 'NAICS determination failed'
      return { result: null, error: message, canRetry: true }
    }
  }
}
